import type { IconName } from './icons.ts';
import type { StatusCondition } from './moves.ts';

/** Clé d'un type élémentaire (« fire », « water »…) ; les types sont définis dans l'administration. */
export type Element = string;

export interface ElementType {
  readonly key: Element;
  readonly label: string;
  readonly icon: IconName;
  readonly color: string;
  /** Types contre lesquels ce type est super efficace. */
  readonly strongAgainst: readonly Element[];
  /** Types contre lesquels ce type est peu efficace. */
  readonly weakAgainst: readonly Element[];
  /** Statuts qui ne peuvent pas toucher un monstre de ce type. */
  readonly statusImmunities: readonly StatusCondition[];
}

export type ElementTypeInput = Omit<ElementType, 'key'>;

export const ELEMENT_TYPE_LIMITS = { label: 20 } as const;

/**
 * Types créés avec la base (modifiables ensuite dans l'administration comme les autres). Chaque type
 * a 2 à 4 cibles super efficaces, et chacun subit au moins un type super efficace.
 */
export const DEFAULT_ELEMENT_TYPES: readonly ElementType[] = [
  { key: 'normal', label: 'Normal', icon: 'type-normal', color: '#a8a29e', strongAgainst: [], weakAgainst: ['rock', 'ghost'], statusImmunities: [] },
  { key: 'fire', label: 'Feu', icon: 'type-fire', color: '#f97316', strongAgainst: ['grass', 'ice', 'astral'], weakAgainst: ['water', 'rock'], statusImmunities: ['burn', 'freeze'] },
  { key: 'water', label: 'Eau', icon: 'type-water', color: '#3b82f6', strongAgainst: ['fire', 'rock'], weakAgainst: ['grass', 'electric'], statusImmunities: ['freeze'] },
  { key: 'grass', label: 'Plante', icon: 'type-grass', color: '#22c55e', strongAgainst: ['water', 'rock'], weakAgainst: ['fire', 'grass', 'toxic', 'air'], statusImmunities: [] },
  { key: 'electric', label: 'Électrik', icon: 'type-electric', color: '#eab308', strongAgainst: ['water', 'air', 'astral'], weakAgainst: ['rock', 'grass'], statusImmunities: ['paralysis'] },
  { key: 'rock', label: 'Roche', icon: 'type-rock', color: '#a16207', strongAgainst: ['fire', 'electric', 'ice', 'air'], weakAgainst: ['water', 'grass', 'fighting'], statusImmunities: [] },
  { key: 'ghost', label: 'Spectre', icon: 'type-ghost', color: '#8b5cf6', strongAgainst: ['ghost', 'normal'], weakAgainst: ['astral'], statusImmunities: [] },
  { key: 'astral', label: 'Astral', icon: 'type-astral', color: '#ec4899', strongAgainst: ['ghost', 'fighting', 'toxic'], weakAgainst: ['fire', 'electric'], statusImmunities: [] },
  { key: 'ice', label: 'Glace', icon: 'type-ice', color: '#a5f3fc', strongAgainst: ['grass', 'air'], weakAgainst: ['fire', 'water', 'ice'], statusImmunities: ['freeze'] },
  { key: 'fighting', label: 'Combat', icon: 'type-fighting', color: '#dc2626', strongAgainst: ['normal', 'rock', 'ice'], weakAgainst: ['ghost', 'air', 'toxic', 'astral'], statusImmunities: [] },
  { key: 'toxic', label: 'Toxik', icon: 'type-toxic', color: '#84cc16', strongAgainst: ['grass', 'fighting'], weakAgainst: ['rock', 'ghost', 'toxic'], statusImmunities: ['poison'] },
  { key: 'air', label: 'Vol', icon: 'type-air', color: '#818cf8', strongAgainst: ['grass', 'fighting'], weakAgainst: ['rock', 'electric'], statusImmunities: [] },
];

/** Clé tirée du nom (« Électrik » → « electrik »), unique parmi `taken`. */
export function elementKeyFromLabel(label: string, taken: ReadonlySet<string>): Element {
  const base = label.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 24) || 'type';
  let key = base;
  for (let n = 2; taken.has(key); n++) key = `${base}-${n}`;
  return key;
}

let registry = new Map<Element, ElementType>();

/** Types connus, chargés depuis la base (serveur) ou reçus du serveur (jeu, administration). */
export const elementTypes = {
  set(types: readonly ElementType[]): void {
    registry = new Map(types.map((type) => [type.key, type]));
  },
  /** Un type supprimé depuis s'affiche sous sa clé, sans faiblesse ni résistance. */
  get: (key: Element): ElementType => registry.get(key)
    ?? { key, label: key, icon: 'type-normal', color: '#a8a29e', strongAgainst: [], weakAgainst: [], statusImmunities: [] },
  keys: (): Element[] => [...registry.keys()],
  all: (): ElementType[] => [...registry.values()],
};

export const isElement = (value: unknown): value is Element => typeof value === 'string' && registry.has(value);

/** Bonus d'une attaque du même type que le monstre qui la lance. */
export const SAME_TYPE_BONUS = 1.5;

export const SUPER_EFFECTIVE = 1.6;
export const NOT_VERY_EFFECTIVE = 0.6;

/** Types d'attaque super efficaces contre `defender`. */
export const weaknessesOf = (defender: Element): Element[] => elementTypes.keys().filter((attacker) => elementTypes.get(attacker).strongAgainst.includes(defender));

/** Types d'attaque peu efficaces contre `defender`. */
export const resistancesOf = (defender: Element): Element[] => elementTypes.keys().filter((attacker) => elementTypes.get(attacker).weakAgainst.includes(defender));

/** Multiplicateur de dégâts d'une attaque de type `attacker` sur un monstre de type `defender`. */
export function effectiveness(attacker: Element, defender: Element): number {
  const { strongAgainst, weakAgainst } = elementTypes.get(attacker);
  if (strongAgainst.includes(defender)) return SUPER_EFFECTIVE;
  if (weakAgainst.includes(defender)) return NOT_VERY_EFFECTIVE;
  return 1;
}

/** Un monstre a un type, et parfois un second. */
export interface Typed {
  element: Element;
  secondElement: Element | null;
}

/** Type dominant d'une équipe : le plus présent (le type principal compte double), le premier rencontré à égalité ; null si vide. */
export function dominantElement(team: readonly Typed[]): Element | null {
  const weights = new Map<Element, number>();
  for (const { element, secondElement } of team) {
    weights.set(element, (weights.get(element) ?? 0) + 2);
    if (secondElement) weights.set(secondElement, (weights.get(secondElement) ?? 0) + 1);
  }
  let best: Element | null = null;
  for (const [element, weight] of weights) if (best === null || weight > (weights.get(best) ?? 0)) best = element;
  return best;
}

/** Types d'un monstre : le premier, puis le second s'il en a un. */
export const typesOf = ({ element, secondElement }: Typed): Element[] => (secondElement ? [element, secondElement] : [element]);

/**
 * Multiplicateur de dégâts d'une attaque de type `attacker` sur un monstre à un ou deux types : le produit de son
 * efficacité contre chacun (deux faiblesses se cumulent, une faiblesse et une résistance se compensent).
 */
export const effectivenessAgainst = (attacker: Element, defender: Typed): number =>
  typesOf(defender).reduce((multiplier, type) => multiplier * effectiveness(attacker, type), 1);

/** Types d'attaque super efficaces et peu efficaces contre ce monstre (ses deux types réunis), avec leur multiplicateur. */
export function matchupsAgainst(defender: Typed): { weak: { element: Element; multiplier: number }[]; resisted: { element: Element; multiplier: number }[] } {
  const all = elementTypes.keys().map((element) => ({ element, multiplier: effectivenessAgainst(element, defender) }));
  return { weak: all.filter(({ multiplier }) => multiplier > 1), resisted: all.filter(({ multiplier }) => multiplier < 1) };
}
